import { FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchIcon } from './SearchIcon';

interface Props {
  initialValue?: string;
  placeholder?: string;
  size?: 'default' | 'hero';
  autoFocus?: boolean;
  onSubmit: (query: string) => void;
  buttonLabel?: string;
}

export function SearchInput({
  initialValue = '',
  placeholder,
  size = 'default',
  autoFocus = false,
  onSubmit,
  buttonLabel,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t('search.placeholder');
  const resolvedButtonLabel = buttonLabel ?? t('search.button');
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);

  // Keep the input in sync if a parent updates initialValue (e.g. navigating
  // between /results?q=foo and /results?q=bar via back/forward). Do NOT overwrite
  // the value while the user is actively typing — that would silently discard
  // their input on an unrelated re-render.
  useEffect(() => {
    if (document.activeElement === inputRef.current) return;
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) return;
    onSubmit(trimmed);
  };

  return (
    <form
      role="search"
      className={`search-input ${size === 'hero' ? 'search-input--hero' : ''}`}
      onSubmit={handleSubmit}
    >
      <SearchIcon aria-hidden />
      <input
        ref={inputRef}
        type="search"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={resolvedPlaceholder}
        aria-label={resolvedPlaceholder}
        autoComplete="off"
        enterKeyHint="search"
      />
      <button type="submit" disabled={value.trim().length === 0}>
        {resolvedButtonLabel}
      </button>
    </form>
  );
}
